import { ContractVariableDefinition } from "../../../Common/Types-DTOs/Contracts";
import { AddressSuggestion } from "../../../Common/Types-DTOs/Address";

/**
 * The form values once the user picks an address suggestion: the address
 * itself, plus whatever the template told this address to fill in.
 *
 * Nothing is cleared when the suggestion lacks a piece. A CCAA always goes
 * into the primary "ccaa" variable, the only one the panel shows and sends
 * (the backend copies it to the rest), and only if that variable offers it.
 *
 * @param addressVariable The "address" variable the suggestion was picked in.
 * @param suggestion The picked suggestion.
 * @param schema The contract's variable schema.
 * @param primaryCcaaKey Key of the "ccaa" variable the panel shows, if any.
 * @param values The form values before picking.
 * @returns The form values after picking.
 */
export const applyAddressAutofill = (
  addressVariable: ContractVariableDefinition,
  suggestion: AddressSuggestion,
  schema: ContractVariableDefinition[],
  primaryCcaaKey: string | undefined,
  values: Record<string, string>
): Record<string, string> => {
  const next = { ...values, [addressVariable.key]: suggestion.address };

  for (const target of addressVariable.autofillTargets ?? []) {
    if (!schema.some((v) => v.key === target.key)) continue;

    if (target.field === "postalCode" && suggestion.postalCode) {
      next[target.key] = suggestion.postalCode;
    } else if (target.field === "municipality" && suggestion.municipality) {
      next[target.key] = suggestion.municipality;
    } else if (target.field === "province" && suggestion.province) {
      next[target.key] = suggestion.province;
    } else if (target.field === "ccaa" && suggestion.ccaa && primaryCcaaKey) {
      const primaryCcaa = schema.find((v) => v.key === primaryCcaaKey);
      const isOffered = primaryCcaa?.ccaaValues?.some((cv) => cv.ccaa === suggestion.ccaa);
      if (isOffered) next[primaryCcaaKey] = suggestion.ccaa;
    }
  }

  return next;
};
